import { For, Show } from "solid-js";
import type { ReportContent, ReportSection } from "@energyplatform/client";
import { locale, t, tOr } from "../i18n.ts";
import {
  formatDateTime,
  formatNumber,
  formatRange,
  formatShare,
} from "../format.ts";
import {
  cellText,
  type Formatters,
  kpiChange,
  kpiText,
  unitText,
} from "../reports.ts";

/**
 * The name of a block's series: the TGE price, or a point with the quantity and channel when one was chosen
 * ("point:Biuro|voltage|phase1" → "Biuro · Napięcie · Faza L1").
 */
export function seriesName(key: string): string | null {
  if (key === "tge") {
    return t("reports.series.price");
  }
  if (!key.startsWith("point:")) {
    return null;
  }
  const [name, attribute, channel] = key.slice(6).split("|");
  return [
    name,
    attribute ? tOr(`attribute.${attribute}`, attribute) : null,
    channel ? tOr(`channel.${channel}`, channel) : null,
  ]
    .filter((x) => x)
    .join(" · ");
}

/** A chart or table label as the reader's calendar shows it: an hour of the day, a day and hour, a day, a month. */
export function periodLabel(l: string): string {
  switch (labelKind(l)) {
    case "hours":
      return l;
    case "months":
      return new Intl.DateTimeFormat(locale(), {
        month: "short",
        year: "numeric",
      }).format(new Date(`${l}-15T12:00:00`));
    case "dayHours":
      return new Intl.DateTimeFormat(locale(), {
        day: "numeric",
        month: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(l));
    default:
      return new Intl.DateTimeFormat(locale(), {
        day: "numeric",
        month: "numeric",
        year: "numeric",
      }).format(new Date(`${l}T12:00:00`));
  }
}

/** Formatting of report figures in the reader's language. */
export function reportFormatters(): Formatters {
  return {
    number: (v, d) => formatNumber(v, locale(), d),
    share: (v) => formatShare(v, locale()),
    time: (iso) => formatDateTime(iso, locale()),
    code: (kind, v) => {
      switch (kind) {
        case "eventKind":
          return t(`events.kind.${v}`);
        case "priority":
          return t(`events.prio.${v}`);
        case "stage":
          return t(`events.stage.${v}`);
        case "connectionState":
          return t(`connectionState.${v}`);
        case "costCategory":
          return t(`finance.category.${v}`);
        case "medium":
          return t(`medium.${v}`);
        case "pointRole":
          return t(`role.point.${v}`);
        case "channel":
          return t(`channel.${v}`);
        case "label":
          return periodLabel(String(v));
        default:
          return t(`reports.row.${v}`);
      }
    },
    label: (k) => {
      const dot = k.indexOf(".");
      const named = dot > 0 ? seriesName(k.slice(dot + 1)) : null;
      return named ?? t(`reports.${k}`);
    },
  };
}

/**
 * A report as a page (prototype "migawka"): always light, also in the dark theme, so it prints and reads like the
 * document that was sent. Figures come from the content as generated; only the language follows the reader.
 */
export function ReportPaper(props: {
  name: string;
  organisation: string;
  from: string;
  to: string;
  content: ReportContent;
  generatedAt?: string;
  version?: string;
  by?: string;
  comment?: string | null;
  mini?: boolean;
}) {
  const f = reportFormatters();
  const period = () =>
    formatRange(new Date(props.from), new Date(props.to), locale());
  const comparison = () =>
    props.content.comparisonFrom && props.content.comparisonTo
      ? formatRange(
          new Date(props.content.comparisonFrom),
          new Date(props.content.comparisonTo),
          locale(),
        )
      : null;
  return (
    <article class={`paper${props.mini ? " mini" : ""}`}>
      <header>
        <span>
          {props.organisation} · {props.content.nodeName}
        </span>
        <span>{period()}</span>
      </header>
      <h4>{props.name}</h4>
      <Show when={props.comment}>
        <p style={{ margin: 0 }}>{props.comment}</p>
      </Show>
      <For each={props.content.notes.filter((n) => n !== "onChangeSensors")}>
        {(n) => (
          <p style={{ margin: 0, color: "var(--warn)" }}>
            {t(`reports.note.${n}`, {
              share: formatShare(props.content.completeness, locale()),
            })}
          </p>
        )}
      </For>
      <For each={props.content.sections}>
        {(s) => (
          <Section
            section={s}
            template={props.content.template}
            comparison={comparison()}
            f={f}
          />
        )}
      </For>
      <footer>
        {[
          props.generatedAt
            ? t("reports.paper.generated", {
                at: formatDateTime(props.generatedAt, locale()),
                by:
                  props.by === "schedule"
                    ? t("reports.bySchedule")
                    : (props.by ?? ""),
              })
            : t("reports.paper.preview"),
          props.version
            ? t("reports.paper.version", { version: props.version })
            : null,
          props.content.completeness === null
            ? null
            : t("reports.paper.completeness", {
                share: formatShare(props.content.completeness, locale()),
              }),
          comparison()
            ? t("reports.paper.comparison", { period: comparison()! })
            : null,
          t("reports.paper.scope", { name: props.content.nodeName }),
          t("reports.paper.timeZone", { zone: props.content.timeZone }),
          props.content.notes.includes("onChangeSensors")
            ? t("reports.note.onChangeSensors")
            : null,
        ]
          .filter((x) => x !== null)
          .join(" · ")}
      </footer>
    </article>
  );
}

function Section(props: {
  section: ReportSection;
  template: string;
  comparison: string | null;
  f: Formatters;
}) {
  const s = () => props.section;
  const title = () =>
    s().title ?? t(`reports.heading.${props.template}.${s().key}`);
  return (
    <>
      <Show when={s().kpis}>
        {(kpis) => (
          <>
            <Show when={s().title}>
              <h5 style={{ margin: "10px 0 0" }}>{s().title}</h5>
            </Show>
            <div class="pk">
              <For each={kpis()}>
                {(k) => {
                  const change = kpiChange(k);
                  return (
                    <div>
                      {props.f.label(`kpi.${k.key}`)}
                      <b>{kpiText(k, props.f)}</b>
                      <span>
                        {k.at
                          ? formatDateTime(k.at, locale())
                          : change
                            ? t(
                                change.kind === "points"
                                  ? "reports.changePoints"
                                  : "reports.changeRelative",
                                {
                                  value:
                                    change.kind === "points"
                                      ? `${change.value >= 0 ? "+" : ""}${formatNumber(change.value, locale(), 0)}`
                                      : `${change.value >= 0 ? "+" : ""}${formatShare(change.value, locale())}`,
                                },
                              )
                            : " "}
                      </span>
                    </div>
                  );
                }}
              </For>
            </div>
          </>
        )}
      </Show>
      <Show when={s().table}>
        {(table) => (
          <div class="psec">
            <h5>{title()}</h5>
            <Show
              when={table().rows.length > 0}
              fallback={<p class="hint">{t("reports.emptyTable")}</p>}
            >
              <div class="scrollx">
                <table class="tbl">
                  <thead>
                    <tr>
                      <For each={table().columns}>
                        {(c) => (
                          <th
                            class={
                              c.kind === "number" || c.kind === "percent"
                                ? "r"
                                : ""
                            }
                          >
                            {props.f.label(`col.${c.key}`)}
                          </th>
                        )}
                      </For>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={table().rows}>
                      {(r) => (
                        <tr>
                          <For each={table().columns}>
                            {(c, i) => (
                              <td
                                class={
                                  c.kind === "number" || c.kind === "percent"
                                    ? "r num"
                                    : ""
                                }
                              >
                                {cellText(c, r[i()] ?? null, props.f)}
                              </td>
                            )}
                          </For>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </div>
            </Show>
          </div>
        )}
      </Show>
      <Show when={s().chart}>
        {(chart) => (
          <div class="psec">
            <h5>
              {title()} · {unitText(chart().unit)}
              {chart().unit2 ? ` / ${unitText(chart().unit2)}` : ""}
            </h5>
            <PaperChart
              chart={chart()}
              labels={
                s().key === "profile"
                  ? "hours"
                  : s().key === "monthly"
                    ? "months"
                    : "days"
              }
              comparison={props.comparison}
            />
          </div>
        )}
      </Show>
    </>
  );
}

const W = 640;
const H = 170;
const PAD = { l: 44, r: 8, t: 8, b: 22 };
const COMBO = [
  "var(--m-el)",
  "var(--warn)",
  "var(--s3)",
  "var(--s4)",
  "var(--s5)",
];

/** What the labels are, from their form: an hour of the day ("07"), a month ("2026-10"), a day, or a day and hour. */
function labelKind(l: string): "hours" | "months" | "days" | "dayHours" {
  return l.length <= 2
    ? "hours"
    : l.length === 7
      ? "months"
      : l.length > 10
        ? "dayHours"
        : "days";
}

/**
 * A small chart for print: bars per day or lines per hour, the comparison period lighter, gaps left empty. A combo
 * chart draws bars and lines together, the lines of another unit (a price) on a right axis.
 */
function PaperChart(props: {
  chart: NonNullable<ReportSection["chart"]>;
  labels: "hours" | "days" | "months";
  comparison: string | null;
}) {
  const c = () => props.chart;
  const combo = () => c().kind === "combo";
  const right = () => combo() && c().series.some((s) => s.axis === "right");
  const padR = () => (right() ? 44 : PAD.r);
  const range = (axis: "left" | "right") => {
    const values = c()
      .series.filter((s) => (s.axis === "right") === (axis === "right"))
      .flatMap((s) => s.values.filter((v): v is number => v !== null));
    const max = Math.max(0, ...values) || 1;
    const min = Math.min(0, ...values);
    return { min, max: max === min ? min + 1 : max };
  };
  const n = () => Math.max(1, c().labels.length);
  const x = (i: number) => PAD.l + ((W - PAD.l - padR()) * (i + 0.5)) / n();
  const y = (v: number, axis: "left" | "right" = "left") => {
    const r = range(axis);
    return PAD.t + (H - PAD.t - PAD.b) * (1 - (v - r.min) / (r.max - r.min));
  };
  const step = () => Math.max(1, Math.ceil(n() / 12));
  // Hours of several days are labelled by their date at midnight only, at most about ten dates.
  const hourly = () =>
    c().labels.length > 0 && labelKind(c().labels[0]!) === "dayHours";
  const dayStep = () =>
    Math.max(
      1,
      Math.ceil(c().labels.filter((l) => l.endsWith("T00:00")).length / 10),
    );
  const shown = (l: string, i: number) =>
    hourly()
      ? l.endsWith("T00:00") &&
        c()
          .labels.slice(0, i)
          .filter((x) => x.endsWith("T00:00")).length %
          dayStep() ===
          0
      : i % step() === 0;
  const label = (l: string) => {
    switch (labelKind(l)) {
      case "hours":
        return l;
      case "months":
        return new Intl.DateTimeFormat(locale(), { month: "short" }).format(
          new Date(`${l}-15T12:00:00`),
        );
      case "dayHours":
        return new Intl.DateTimeFormat(locale(), {
          day: "numeric",
          month: "numeric",
        }).format(new Date(l));
      default:
        return new Intl.DateTimeFormat(locale(), {
          day: "numeric",
          month: "numeric",
        }).format(new Date(`${l}T12:00:00`));
    }
  };
  const colors = () =>
    combo()
      ? COMBO
      : ["var(--m-el)", "var(--line-2)", "var(--s3)", "var(--s4)"];
  const bars = () =>
    c().series.filter((s) => (combo() ? s.kind === "bar" : c().kind === "bars"))
      .length || 1;
  const bw = () => ((W - PAD.l - padR()) / n()) * 0.8;
  const line = (values: (number | null)[], axis: "left" | "right") =>
    values
      .map((v, i) =>
        v === null
          ? ""
          : `${i === 0 || values[i - 1] === null ? "M" : "L"}${x(i).toFixed(1)} ${y(v, axis).toFixed(1)}`,
      )
      .join("");
  const isBar = (s: { kind?: string | null }) =>
    combo() ? s.kind === "bar" : c().kind === "bars";
  const ticks = (axis: "left" | "right") => {
    const r = range(axis);
    return [r.min, r.min + (r.max - r.min) / 2, r.max];
  };
  return (
    <>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", display: "block" }}
        role="img"
        aria-label={c().unit}
      >
        <For each={ticks("left")}>
          {(v) => (
            <>
              <line
                x1={PAD.l}
                x2={W - padR()}
                y1={y(v)}
                y2={y(v)}
                stroke="var(--line)"
              />
              <text
                x={PAD.l - 4}
                y={y(v) + 3}
                text-anchor="end"
                style={{ "font-size": "9px", fill: "var(--muted)" }}
              >
                {formatNumber(v, locale(), 0)}
              </text>
            </>
          )}
        </For>
        <Show when={right()}>
          <For each={ticks("right")}>
            {(v) => (
              <text
                x={W - padR() + 4}
                y={y(v, "right") + 3}
                style={{ "font-size": "9px", fill: "var(--warn)" }}
              >
                {formatNumber(v, locale(), 0)}
              </text>
            )}
          </For>
        </Show>
        <For each={c().labels}>
          {(l, i) => (
            <Show when={shown(l, i())}>
              <text
                x={x(i())}
                y={H - 6}
                text-anchor="middle"
                style={{ "font-size": "9px", fill: "var(--muted)" }}
              >
                {label(l)}
              </text>
            </Show>
          )}
        </For>
        <For each={[...c().series].reverse()}>
          {(s) => {
            const k = c().series.indexOf(s);
            const color = colors()[k] ?? "var(--muted)";
            const axis = s.axis === "right" ? "right" : "left";
            const nth = c().series.filter(isBar).indexOf(s);
            return isBar(s) ? (
              <For each={s.values}>
                {(v, i) => (
                  <Show when={v !== null}>
                    <rect
                      x={
                        combo()
                          ? x(i()) - bw() / 2 + (bw() / bars()) * nth
                          : x(i()) - bw() / 2 + (k === 0 ? bw() * 0.25 : 0)
                      }
                      width={combo() ? bw() / bars() : bw() * 0.75}
                      y={Math.min(y(v!, axis), y(0, axis))}
                      height={Math.abs(y(0, axis) - y(v!, axis))}
                      fill={color}
                      opacity={combo() ? 0.75 : 1}
                    />
                  </Show>
                )}
              </For>
            ) : (
              <path
                d={line(s.values, axis)}
                fill="none"
                stroke={color}
                stroke-width={k === 0 || combo() ? 2 : 1.5}
                stroke-dasharray={
                  combo()
                    ? k > 1
                      ? "4 3"
                      : undefined
                    : k === 0
                      ? undefined
                      : "4 3"
                }
              />
            );
          }}
        </For>
      </svg>
      <div
        style={{
          display: "flex",
          gap: "12px",
          "flex-wrap": "wrap",
          "font-size": "10px",
          color: "var(--muted)",
        }}
      >
        <For each={c().series}>
          {(s, k) => (
            <span>
              <i
                style={{
                  display: "inline-block",
                  width: "10px",
                  height: isBar(s) ? "8px" : "2px",
                  "vertical-align": isBar(s) ? undefined : "middle",
                  background: colors()[k()] ?? "var(--muted)",
                  "margin-right": "4px",
                }}
              />
              {s.key === "comparison" && props.comparison
                ? t("reports.series.comparisonOf", {
                    period: props.comparison,
                  })
                : (seriesName(s.key) ?? t(`reports.series.${s.key}`))}
              {s.axis === "right" && c().unit2
                ? ` (${unitText(c().unit2)})`
                : ""}
            </span>
          )}
        </For>
      </div>
    </>
  );
}
